import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../lib/Seo";
import RevealText from "../lib/RevealText";
import Reveal from "../lib/Reveal";
import Marquee from "../components/Marquee";
import { findItinerary } from "../data/itineraries";
import { ArrowUpRight } from "lucide-react";

const pillars = [
  {
    num: "01",
    title: "Community-Led",
    body: "Every day of this journey is composed with, and financially supports, the communities on whose land we travel — Maasai, Samburu, Turkana.",
  },
  {
    num: "02",
    title: "Wildlife Positive",
    body: "You will spend real time with the rangers and researchers protecting Kenya's most endangered species — on foot, on patrol, on record.",
  },
  {
    num: "03",
    title: "Carbon Positive",
    body: "Every emission, offset twice over — through verified reforestation on the very conservancies you visit.",
  },
  {
    num: "04",
    title: "Land Restoration",
    body: "You plant indigenous species on rehabilitated grazing land. It is not symbolic; it is measured, monitored, and audited.",
  },
];

export default function SignatureJourney() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const journey = findItinerary("the-regenerative-safari");

  return (
    <>
      <Seo
        title="The Regenerative Safari 2026 | Njia Safaris — Signature Journey"
        description="Njia Safaris' 2026 Signature Journey — a fourteen-day carbon-positive safari through Lewa, Borana, the Northern Rangelands, the Masai Mara and Amboseli. Travel that returns more than it takes."
        image={journey?.hero}
        path="/signature-journey"
      />
      <Nav />

      {/* HERO */}
      <section
        ref={heroRef}
        className="relative h-[100svh] overflow-hidden section-dark"
        data-testid="signature-hero"
      >
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1523805009345-7448845a9e53?auto=format&fit=crop&w=2400&q=85"
            alt="Green savannah — calving season"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/25 to-black/70" />
        <div className="grain-overlay" />

        <div className="relative z-10 h-full container-x flex flex-col justify-end pb-16 md:pb-24">
          <span
            className="inline-block mb-6 mono text-[10px] uppercase tracking-[0.28em] px-3 py-1.5 border w-max"
            style={{ borderColor: "var(--gold)", color: "var(--gold-3)" }}
          >
            2026 Signature Journey
          </span>
          <RevealText
            as="h1"
            className="h-display text-[13vw] md:text-[8.5vw] text-[color:var(--ivory)] max-w-6xl"
            lines={["The Regenerative", <em key="e" className="italic text-[color:var(--gold-3)]">Safari.</em>]}
          />
          <p className="mt-8 font-italic-display text-2xl md:text-3xl text-[color:var(--sand)] max-w-3xl">
            Fourteen days of travel that returns more than it takes.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/contact" state={{ journey: "The Regenerative Safari (2026 Signature)" }} className="btn-gold" data-testid="signature-cta-top">
              Reserve Your Place
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
            <a href="#pillars" className="btn-ghost btn-ghost-invert">
              The four pillars
            </a>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="section-y container-x" data-testid="signature-manifesto">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="eyebrow mb-6">The philosophy</p>
            <RevealText
              as="h2"
              className="h-display text-5xl md:text-6xl"
              lines={["A safari that", <em key="e" className="italic text-[color:var(--forest)]">plants.</em>]}
            />
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-xl md:text-2xl font-display leading-snug mb-8">
              For decades, safari has been extractive. You arrive, you see, you leave. The land pays.
            </p>
            <p className="text-lg leading-relaxed text-[color:var(--ink)]/85 max-w-xl mb-6">
              The Regenerative Safari is our response. A fourteen-day journey across Kenya&rsquo;s most storied conservancies —
              designed with the people who protect them, priced to return more than it takes.
            </p>
            <p className="text-lg leading-relaxed text-[color:var(--ink)]/85 max-w-xl">
              You will walk beside rangers, plant on rehabilitated ground, and sit with elders. The luxury is real —
              Lewa, Borana, Sala&rsquo;s. The impact is measured. The intention is quiet.
            </p>
          </div>
        </div>
      </section>

      {/* EDITORIAL IMAGE */}
      <section className="container-x mb-24" data-testid="signature-editorial">
        <Reveal>
          <div className="img-frame aspect-[16/9]">
            <img
              src="https://images.unsplash.com/photo-1523805009345-7448845a9e53?auto=format&fit=crop&w=2400&q=85"
              alt="The savannah at dawn"
            />
          </div>
        </Reveal>
        <p className="mt-6 mono text-[11px] uppercase tracking-[0.28em] text-[color:var(--ink)]/60 max-w-xl">
          Lewa Wildlife Conservancy · Day 3 · Ranger patrol at dawn
        </p>
      </section>

      {/* PILLARS */}
      <section id="pillars" className="section-forest py-24 md:py-32" data-testid="signature-pillars">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-4">
              <p className="eyebrow mb-4">The four pillars</p>
              <RevealText
                as="h2"
                className="h-display text-4xl md:text-5xl text-[color:var(--ivory)]"
                lines={["What makes it", <em key="e" className="italic text-[color:var(--gold-3)]">regenerative.</em>]}
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-10 md:gap-16">
            {pillars.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.06}>
                <div className="border-t border-[color:var(--line-invert)] pt-6">
                  <span className="chapter-num">{p.num}</span>
                  <h3 className="font-display text-3xl md:text-4xl text-[color:var(--ivory)] mt-4">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-[color:var(--sand)] max-w-md leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ROUTE / DAYS */}
      {journey && (
        <section className="section-y container-x" data-testid="signature-days">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-4">
              <p className="eyebrow mb-4">The path</p>
              <RevealText
                as="h2"
                className="h-display text-4xl md:text-5xl"
                lines={["Fourteen days,", <em key="e" className="italic text-[color:var(--forest)]">six landscapes.</em>]}
              />
            </div>
            <div className="md:col-span-7 md:col-start-6">
              <div className="route-line mb-8">
                {journey.route.map((r, i) => (
                  <div key={r} className="route-line">
                    <span className="route-pill">{r}</span>
                    {i < journey.route.length - 1 && <span className="route-dot" />}
                  </div>
                ))}
              </div>
              <p className="text-lg leading-relaxed max-w-xl">
                From Nairobi to Lewa&rsquo;s rangers, through Borana&rsquo;s tree-planting, into the Northern Rangelands
                with the Samburu, across the Mara&rsquo;s cats, and closing with Amboseli&rsquo;s elephant matriarchs.
              </p>
            </div>
          </div>

          <div>
            {journey.days.map((d, i) => (
              <Reveal key={d.day} delay={i * 0.02}>
                <div className="grid grid-cols-12 gap-6 md:gap-10 py-6 border-t border-[color:var(--line)]">
                  <div className="col-span-3 md:col-span-2">
                    <span className="mono text-[11px] uppercase tracking-[0.28em] text-[color:var(--forest)]">
                      Day {String(d.day).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="col-span-9 md:col-span-4">
                    <h3 className="font-display text-xl md:text-2xl leading-tight">{d.title}</h3>
                  </div>
                  <div className="col-span-12 md:col-span-6">
                    <p className="text-[15px] leading-relaxed text-[color:var(--ink)]/85">{d.detail}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <div className="hairline" />
          </div>
        </section>
      )}

      {/* MARQUEE */}
      <section className="section-dark py-16">
        <Marquee items={["Lewa", "Borana", "Sera", "Samburu", "Masai Mara", "Amboseli", "2026"]} />
      </section>

      {/* CTA */}
      <section className="section-y container-x" data-testid="signature-cta">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <p className="eyebrow mb-6">Reserve your place</p>
            <RevealText
              as="h2"
              className="h-display text-5xl md:text-7xl"
              lines={["Only twelve", <em key="e" className="italic text-[color:var(--forest)]">guests in 2026.</em>]}
            />
          </div>
          <div className="md:col-span-5">
            <p className="text-lg leading-relaxed mb-8">
              The Regenerative Safari runs on select dates in 2026, with only twelve guests per departure to protect the
              intimacy and integrity of the experience.
            </p>
            <Link to="/contact" state={{ journey: "The Regenerative Safari (2026 Signature)" }} className="btn-gold" data-testid="signature-cta-bottom">
              Enquire About 2026 Dates
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
